
import { unities, unitiesDecimals } from "../constants/unities.constants";
import { AddressData } from "../types/address";
import { isValidDate } from "./dateValidator";
import { numberToFixedHalfDown } from "./numberToFixedHalfDown";

export const formatCurrency = (value: number, showFractions?: boolean) => {
  showFractions = showFractions ?? true

  const formatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: showFractions ? 2 : 0,
  })
  return formatter.format(value)
}

export const formatPercentage = (value: number) => {
  return `${(value * 100).toFixed(2).replace('.', ',')} %`
}

export const formatCnpj = (cnpj: string) => {
  return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}

export const formatCpf = (cpf: string) => {
  return cpf?.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

export const formatCnpjCpf = (value: string) => {
  if (value?.length > 11) {
    return formatCnpj(value)
  }

  return formatCpf(value)
}

export const formatDateTime = (date: Date) => {
  if (!isValidDate(date)) return '';

  const formattedDate = formatDate(date);
  const formattedHours = String(date.getHours()).padStart(2, '0');
  const formattedMinutes = String(date.getMinutes()).padStart(2, '0');

  return `${formattedDate} às ${formattedHours}:${formattedMinutes}`;
};

export const formatTime = (date: Date, showSeconds: boolean = true) => {
  if (!isValidDate(date)) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: showSeconds ? '2-digit' : undefined,
    hour12: false
  }).format(date)
};


export const formatDateTimeUTC = (date: Date) => {
  if (!isValidDate(date)) return '';

  const formattedDateTime = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'shortOffset',
    timeZone: 'America/Sao_Paulo',
  }).format(date);

  const [formattedDate, time, timezone] = formattedDateTime.split(' ');

  return `${formattedDate.replace(',', '')} às ${time} (${timezone})`
};

export const formatDate = (date: Date, formatYearWithTwoDigits?: boolean) => {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: formatYearWithTwoDigits ? '2-digit' : 'numeric',
  }).format(date)
}

export const formatDateInLocalTimezone = (date: string, formatYearWithTwoDigits?: boolean) => {
  const dateWithLocalMidnight = new Date(`${date}T00:00:00`)

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: formatYearWithTwoDigits ? '2-digit' : 'numeric',
  }).format(dateWithLocalMidnight)
}

export const formatDateRange = (startDate: Date, endDate: Date) => {
  return `${formatDate(startDate)} à ${formatDate(endDate)}`
}

export const formatUnit = (value: string | number, unit: string) => {
  return `${value} ${unities[unit] || ''}`
}

export const formatLicensePLate = (value: string) => {
  const match = value.match(/^([a-zA-Z]{3})(\d{4})$/);
  const uppercasedMatch = match?.map((piece) => piece?.toUpperCase())

  return `${uppercasedMatch?.[1]}-${uppercasedMatch?.[2]}`
}

export const formatCardNumber = (cardNumber: string | number) => {
  const cardNumberStr = cardNumber.toString();
  const maskedPart = cardNumberStr.padStart(16, '*');
  return maskedPart.replace(/(.{4})/g, '$1 ').trim();
}

export const formatDecimal = (value: string | number, decimals = 3) => {
  const formatter = new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  });

  return formatter.format(Number(numberToFixedHalfDown(Number(value), decimals)));
}

export const formatPhoneNumber = (phone: string) => {
  if(phone?.length === 11) {
    return phone?.replace(/^(\d{2})(\d{5}|\d{4})(\d{4})$/, '($1) $2-$3')
  }

  return phone?.replace(/^(\d{2})(\d{2})(\d{5}|\d{4})(\d{4})$/, '+$1 ($2) $3-$4')
}

export const formatDateWithTimezone = (date: Date, timeZone: string, showTime = false) => {
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timeZone,
  }

  if (showTime) {
    options.hour = '2-digit'
    options.minute = '2-digit'
  }

  return date.toLocaleDateString('pt-BR', options).replace(',', '');

}

export const truncateText = (text: string, maxLength: number, suffix: string = '...') => {
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}${suffix}`;
}

export const formatDecimalUnit = (
  value: string | number,
  unit?: string | null,
  groundTruth = false,
  fractionDigitsOverride?: number,
) => {
  if (!unit) {
    const decimalsWithoutUnit = fractionDigitsOverride ?? (groundTruth ? 3 : 2);
    return formatDecimal(value, decimalsWithoutUnit);
  }

  const unitDecimals = unitiesDecimals[unit as keyof typeof unitiesDecimals];
  let decimals: number | undefined = unitDecimals;

  if (typeof fractionDigitsOverride === 'number') {
    decimals = fractionDigitsOverride;
  } else if (groundTruth && unitDecimals) {
    decimals = 3;
  }

  return formatUnit(formatDecimal(value, decimals), unit);
}


export const formatDecimalWithGroupedUnit = <T>(
  value: string | number,
  items: T[] | undefined,
  unitExtrator: (item: T) => string,
  groundTruth = false
) => {
  if (!items || items.length === 0) {
    return formatDecimalUnit(value, undefined, groundTruth);
  }

  const firstUnit = unitExtrator(items[0]);
  const isEveryUnitEqual = items.every(item => unitExtrator(item) === firstUnit);

  return formatDecimalUnit(value, isEveryUnitEqual ? firstUnit : undefined, groundTruth);
}

export const formatAddressString = (address: Partial<AddressData> | undefined) => {
  if (!address) {
    return '';
  }

  return `${address.street || '--'}, ${address.number ?? '--'} - ${address.city?.state?.acronym || '--'}, ${address.city?.display_name || '--'}`;
}
